import { BaseError } from "@/domains/errors";
import { api } from "@/modules/api";
import { uploadProfilePhoto } from "@/modules/file/uploadProfilePhoto";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { isAppleProvider, isStripeProvider } from "@/modules/subscriptions/provider";
import { toastModule } from "@/modules/toast";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, makeObservable, observable, computed, runInAction } from "mobx";

type PatchMeAccountRequestBody = {
  profile_display_name?: string;
  profile_photo_uploaded_file_id?: string | null;
};

type PatchMeAccountResponse = {
  data?: {
    profile_display_name: string;
    profile_photo_url: string | null;
  };
  error?: unknown;
};

export class SettingsAccountManagementPageStore extends AppSubStore {
  public isDeleteAccountModalOpen = false;
  public isDeletingAccount = false;
  public isUpdatingDisplayName = false;
  public isUpdatingProfilePhoto = false;
  public hasRenewingSubscription = false;
  public isLoading = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);
    makeObservable(this, {
      isDeleteAccountModalOpen: observable,
      isDeletingAccount: observable,
      isUpdatingDisplayName: observable,
      isUpdatingProfilePhoto: observable,
      hasRenewingSubscription: observable,
      currentAccountEmail: computed,
      currentDisplayName: computed,
      refresh: action,
      openDeleteAccountModal: action,
      closeDeleteAccountModal: action,
      handleDeleteAccount: action,
      handleUpdateDisplayName: action,
      handleUpdateProfilePhoto: action,
      isLoading: observable,
    });
  }

  openDeleteAccountModal = () => {
    this.isDeleteAccountModalOpen = true;
  };

  closeDeleteAccountModal = () => {
    this.isDeleteAccountModalOpen = false;
  };

  refresh = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
    });

    await Promise.all([this.store.platformInfo.fetchPlatformInfo()]);

    const billingSubscription = this.store.subscriptions.billingSubscription;
    runInAction(() => {
      if (!billingSubscription) {
        this.hasRenewingSubscription = false;
        this.isLoading = false;
        return;
      }

      if (
        billingSubscription.isActivePaid &&
        !billingSubscription.isEnding &&
        (isAppleProvider(billingSubscription.provider) ||
          isStripeProvider(billingSubscription.provider))
      ) {
        this.hasRenewingSubscription = true;
        this.isLoading = false;
        return;
      }

      this.hasRenewingSubscription = false;
      this.isLoading = false;
    });
  };

  get currentAccountEmail() {
    return this.store.account.myAccount.profileEmailAddress;
  }

  get currentDisplayName() {
    return this.store.account.myAccount.profileDisplayName;
  }

  handleDeleteAccount = async () => {
    try {
      this.isDeletingAccount = true;
      const response = await api.delete("/v2/me/account");

      if (response.data) {
        this.store.navigation.goToLogOut();
      }

      if (response.error) {
        const err = new BaseError({
          message: "Failed to delete account.",
          info: { result: objectModule.safeAsJson(response) },
        });
        throw err;
      }
    } catch (unknownErr: unknown) {
      const error = unknownErr as BaseError;

      toastModule.triggerToast({
        content: "Failed to delete account. Please try again or contact support.",
      });

      logger.error({
        message: "[SettingsAccountManagementPageStore][handleDeleteAccount]: Error.",
        info: { error: objectModule.safeAsJson({ ...error }) },
      });
    } finally {
      this.isDeletingAccount = false;
    }
  };

  handleUpdateDisplayName = async (newDisplayName: string) => {
    try {
      this.isUpdatingDisplayName = true;
      const response = await patchMeAccount({
        profile_display_name: newDisplayName,
      });

      if (response.data) {
        runInAction(() => {
          this.store.account.myAccount.profileDisplayName = response.data!.profile_display_name;
        });
      }

      if (response.error) {
        const err = new BaseError({
          message: "Failed to update display name.",
          info: { result: objectModule.safeAsJson(response) },
        });
        throw err;
      }
    } catch (unknownErr: unknown) {
      const error = unknownErr as BaseError;

      toastModule.triggerToast({
        content: "Failed to update display name. Please try again.",
      });

      logger.error({
        message: "[SettingsAccountManagementPageStore][handleUpdateDisplayName]: Error.",
        info: { error: objectModule.safeAsJson({ ...error }) },
      });
    } finally {
      this.isUpdatingDisplayName = false;
    }
  };

  handleUpdateProfilePhoto = async (file: File): Promise<boolean> => {
    try {
      this.isUpdatingProfilePhoto = true;
      const uploadResult = await uploadProfilePhoto({ file });

      const response = await patchMeAccount({
        profile_photo_uploaded_file_id: uploadResult.uploadedFileId,
      });

      if (response.data) {
        runInAction(() => {
          this.store.account.myAccount.profilePhotoUrl = response.data!.profile_photo_url ?? null;
        });

        return true;
      }

      if (response.error) {
        const err = new BaseError({
          message: "Failed to update profile photo.",
          info: { result: objectModule.safeAsJson(response) },
        });
        throw err;
      }

      return false;
    } catch (unknownErr: unknown) {
      const error = unknownErr as BaseError;

      toastModule.triggerToast({
        content: "Failed to update profile photo. Please try again.",
      });

      logger.error({
        message: "[SettingsAccountManagementPageStore][handleUpdateProfilePhoto]: Error.",
        info: { error: objectModule.safeAsJson({ ...error }) },
      });

      return false;
    } finally {
      this.isUpdatingProfilePhoto = false;
    }
  };
}

const patchMeAccount = async (body: PatchMeAccountRequestBody): Promise<PatchMeAccountResponse> => {
  const patchAccount = api.patch as unknown as (
    path: "/v2/me/account",
    options: { body: PatchMeAccountRequestBody }
  ) => Promise<PatchMeAccountResponse>;

  // The backend supports PATCH /v2/me/account, but this generated schema is
  // stale in mem-client until codegen is rerun from the merged backend.
  return patchAccount("/v2/me/account", { body });
};
